import { formatDate } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import { DigisubservicesService } from '../services/digisubservices.service';

interface RepRecord {
  id: number;
  oder_no: string;
  booking_date: string;
  rep_name: string;
  rep_mob_no: string;
  office_rep_name: string;
  book_seller_name: string;
  subscribers_no: string;
  voucher_no: string;
  recipt_amt: number;
  recipt_no: string;
  is_complimentary: string;
  book_name: string;
  total_copies: number;
  shipping_charges: number;
  book_price: number;
  sale_price: number;
  total: number;
  received_amt: number;
  total_price: number;
  remaining_amt: number;
}

interface RepGroup {
  rep_name: string;
  records: RepRecord[];
  totalBookCopies: number;
  totalCompCopies: number;
  totalAmount: number;
  totalReceived: number;
  totalPending: number;
  totalShipping: number;
  totalNet: number;
}

interface OfficeGroup {
  office_rep_name: string;
  reps: RepGroup[];
  // Office-level aggregates, computed once when data is loaded
  // (Angular template interpolation cannot run arrow-function .reduce() calls,
  // so these must be precomputed here rather than inline in the HTML).
  totalBookCopies?: number;
  totalCompCopies?: number;
  totalAmount?: number;
  totalReceived?: number;
  totalPending?: number;
  totalShipping?: number;
  totalNet?: number;
}

interface GrandSummary {
  totalBookCopies: number;
  totalCompCopies: number;
  totalAmount: number;
  totalReceived: number;
  totalPending: number;
  totalShipping: number;
  totalNet: number;
}

interface AllocationRow {
  name: string;
  amount: number;
}

@Component({
  selector: 'app-book-seller-print-order-statementgranth',
  templateUrl: './book-seller-print-order-statementgranth.component.html',
  styleUrl: './book-seller-print-order-statementgranth.component.scss'
})
export class BookSellerPrintOrderStatementgranthComponent implements OnInit {
  today = new Date();

  startDate!: Date;
  endDate!: Date;

  book_name = '';
  saving = false;
  loading = false;

  // ============ LEDGER DATA FROM API ============
  officeGroups: OfficeGroup[] = [];
  grandSummary: GrandSummary = {
    totalBookCopies: 0,
    totalCompCopies: 0,
    totalAmount: 0,
    totalReceived: 0,
    totalPending: 0,
    totalShipping: 0,
    totalNet: 0
  };

  // filters (optional - wire up if you have UI for these)
  rep_name: any = null;
  office_rep_name: any = null;
  book_seller_name: any = null;
  complimentaryFilter: string = 'ALL';

  // ============ MANUAL ALLOCATION TABLE (नोंदणी वाटप) ============
  // default rows used only when nothing is found in localStorage
  private defaultAllocationRows: AllocationRow[] = [
    { name: 'नोंदणी', amount: 0 },
    { name: 'जाहिरात', amount: 0 },
    { name: 'साहित्य', amount: 0 },
    { name: 'संपादकीय', amount: 0 },
    { name: 'प्रकाशन', amount: 0 },
    { name: 'कार्यालय', amount: 0 }
  ];

  allocationRows: AllocationRow[] = [];
  totalAllocation = 0;
  category: any;

  // localStorage key — unique per book, so different print orders don't overwrite each other
  private get allocationStorageKey(): string {
    return `print_order_allocation_${this.book_name || 'default'}`;
  }

  constructor(
    private service: DigisubservicesService,
    private dialogRef: MatDialogRef<BookSellerPrintOrderStatementgranthComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.book_name = this.data?.bookwise || '';
    this.category= this.data?.category || '';

    if (this.data?.issuedate) {
      const issueDay = new Date(this.data.issuedate);
      this.endDate = issueDay;

      const weekFirstDay = issueDay.getDate() - issueDay.getDay();
      const start = new Date(issueDay);
      start.setDate(weekFirstDay - 6);

      this.startDate = start;
    }

    this.loadAllocationFromStorage();
    this.getBookSellerReport();
  }

  // ================= LEDGER API =================
  getBookSellerReport(): void {
    this.loading = true;

    const payload = {
      start_date: this.startDate ? formatDate(this.startDate, 'yyyy-MM-dd', 'en-IN') : '',
      end_date: this.endDate ? formatDate(this.endDate, 'yyyy-MM-dd', 'en-IN') : '',
      rep_name: this.rep_name,
      office_rep_name: this.office_rep_name,
      book_seller_name: this.book_seller_name,
      book_name: this.book_name,
      complimentaryFilter: this.complimentaryFilter
    };

    this.service.getBookSellerReport(payload).subscribe({
      next: (res: any) => {
        this.loading = false;

        if (res.success) {
          this.officeGroups = res.office_representatives || [];
          this.grandSummary = res.grand_summary || this.grandSummary;
          this.computeOfficeTotals();
        } else {
          console.error('API error:', res.error || res);
        }
      },
      error: (err) => {
        this.loading = false;
        console.error(err);
      }
    });
  }

  /**
   * Sums each rep's totals up onto its parent office group.
   * Done here (not inline in the template) because Angular's template
   * interpolation parser does not support arrow-function expressions
   * like reps.reduce((s, r) => ...) — that silently breaks the binding.
   */
  private computeOfficeTotals(): void {
    for (const office of this.officeGroups) {
      const reps = office.reps || [];

      office.totalBookCopies = reps.reduce((sum, r) => sum + (Number(r.totalBookCopies) || 0), 0);
      office.totalCompCopies = reps.reduce((sum, r) => sum + (Number(r.totalCompCopies) || 0), 0);
      office.totalAmount = reps.reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0);
      office.totalReceived = reps.reduce((sum, r) => sum + (Number(r.totalReceived) || 0), 0);
      office.totalPending = reps.reduce((sum, r) => sum + (Number(r.totalPending) || 0), 0);
      office.totalShipping = reps.reduce((sum, r) => sum + (Number(r.totalShipping) || 0), 0);
      office.totalNet = reps.reduce((sum, r) => sum + (Number(r.totalNet) || 0), 0);
    }
  }

  // ================= ALLOCATION TABLE (MANUAL + LOCAL STORAGE) =================

  /** Load saved allocation rows from localStorage, falling back to defaults */
  loadAllocationFromStorage(): void {
    const saved = localStorage.getItem(this.allocationStorageKey);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        this.allocationRows = Array.isArray(parsed) && parsed.length
          ? parsed
          : this.cloneDefaultRows();
      } catch (e) {
        console.error('Failed to parse saved allocation, using defaults', e);
        this.allocationRows = this.cloneDefaultRows();
      }
    } else {
      this.allocationRows = this.cloneDefaultRows();
    }

    this.onAllocationChange();
  }

  private cloneDefaultRows(): AllocationRow[] {
    return this.defaultAllocationRows.map(row => ({ ...row }));
  }

  /** Persist current allocation rows to localStorage */
  saveAllocationToStorage(): void {
    localStorage.setItem(
      this.allocationStorageKey,
      JSON.stringify(this.allocationRows)
    );
  }

  onAllocationChange() {
    this.totalAllocation = this.allocationRows.reduce(
      (sum, row) => sum + Number(row.amount || 0),
      0
    );
    this.saveAllocationToStorage();
  }

  addAllocationRow() {
    this.allocationRows.push({ name: '', amount: 0 });
    this.saveAllocationToStorage();
  }

  removeAllocationRow(index: number) {
    this.allocationRows.splice(index, 1);
    this.onAllocationChange();
  }

  /** Optional: reset allocation back to the six default rows */
  resetAllocation(): void {
    this.allocationRows = this.cloneDefaultRows();
    this.onAllocationChange();
  }

  // ================= EXPORT =================
  exportexcel(fileName: string): void {
    const element = document.getElementById('print-section');
    if (!element) {
      console.error('Print section not found!');
      return;
    }
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, fileName + '.xlsx');
  }

  // ================= SAVE =================
  save() {
    if (this.officeGroups.length === 0 && this.allocationRows.every(a => !a.name && !a.amount)) {
      alert('जतन करण्यासाठी काहीही डेटा उपलब्ध नाही');
      return;
    }

    this.saving = true;

    const payload = {
      book_name: this.book_name,
      start_date: this.startDate ? formatDate(this.startDate, 'yyyy-MM-dd', 'en-IN') : '',
      end_date: this.endDate ? formatDate(this.endDate, 'yyyy-MM-dd', 'en-IN') : '',
      generated_on: formatDate(this.today, 'yyyy-MM-dd', 'en-IN'),

      office_representatives: this.officeGroups,
      grand_summary: this.grandSummary,

      allocation: this.allocationRows,
      total_allocation: this.totalAllocation
    };

    console.log('FINAL SAVE PAYLOAD =>', payload);

    this.service.savePrintOrder1(payload).subscribe({
      next: (res: any) => {
        this.saving = false;
        if (res.success) {
          alert('Print Order जतन झाला (ID: ' + res.master_id + ')');
          this.saveAllocationToStorage(); // keep local copy in sync after successful save
          this.dialogRef.close(true);
        } else {
          alert(res.msg || 'Save failed');
        }
      },
      error: (err) => {
        this.saving = false;
        console.error(err);
        alert('Server error while saving');
      }
    });
  }

  // ================= CLOSE =================
  closeDialog() {
    this.dialogRef.close();
  }
}